// src/components/hollo-inline-recorder.ts
import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { holloInlineRecorderStyles } from "./hollo-inline-recorder-styles";

type RecPhase = "recording" | "stopped" | "unsupported" | "error";

@customElement("hollo-inline-recorder")
export class HolloInlineRecorder extends LitElement {
  /** When true, recorder renders and auto-starts */
  @property({ type: Boolean }) open = false;
  /** Optional max seconds for progress bar width calc (cosmetic) */
  @property({ type: Number }) bands = 16;

  @state() private phase: RecPhase = "stopped";
  @state() private seconds = 0;
  @state() private errMsg = "";
  @state() private previewUrl = "";
  @state() private mimeType = "audio/webm";
  /** Live levels (0-100) for the EQ bars */
  @state() private levels: number[] = Array(16).fill(0);

  private stream?: MediaStream;
  private rec?: MediaRecorder;
  private chunks: BlobPart[] = [];
  private t?: number; // seconds timer

  // Web Audio nodes for accurate visualization
  private audioCtx?: AudioContext;
  private analyser?: AnalyserNode;
  private sourceNode?: MediaStreamAudioSourceNode;
  private raf?: number;

  connectedCallback() {
    super.connectedCallback();
  }
  disconnectedCallback() {
    this.stopRecording();
    this.cleanupPreview();
    super.disconnectedCallback();
  }

  private XIcon(size: number) {
    return html`<svg
      xmlns="http://www.w3.org/2000/svg"
      width="${size}"
      height="${size}"
      viewBox="0 0 17 17"
      fill="none"
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M8.75229 10.487L14.0553 15.79C14.3367 16.0714 14.7183 16.2295 15.1163 16.2295C15.5142 16.2295 15.8959 16.0714 16.1773 15.79C16.4587 15.5086 16.6168 15.1269 16.6168 14.729C16.6168 14.331 16.4587 13.9494 16.1773 13.668L10.8723 8.36498L16.1763 3.06198C16.3156 2.92265 16.426 2.75725 16.5014 2.57523C16.5767 2.3932 16.6155 2.19812 16.6154 2.00112C16.6154 1.80413 16.5765 1.60906 16.5011 1.42708C16.4257 1.24509 16.3151 1.07975 16.1758 0.940478C16.0365 0.801211 15.8711 0.690752 15.689 0.615406C15.507 0.540061 15.3119 0.501305 15.1149 0.501351C14.9179 0.501398 14.7229 0.540245 14.5409 0.615677C14.3589 0.691108 14.1936 0.801646 14.0543 0.940978L8.75229 6.24398L3.44929 0.940978C3.31099 0.797649 3.14552 0.683299 2.96255 0.604601C2.77959 0.525902 2.58278 0.484431 2.38361 0.482607C2.18444 0.480783 1.98691 0.518643 1.80253 0.593977C1.61815 0.669311 1.45062 0.780612 1.30971 0.921384C1.16881 1.06216 1.05735 1.22958 0.981842 1.41389C0.906333 1.5982 0.868287 1.7957 0.869924 1.99487C0.87156 2.19404 0.912845 2.39088 0.991371 2.57393C1.0699 2.75697 1.18409 2.92254 1.32729 3.06098L6.63229 8.36498L1.32829 13.669C1.18509 13.8074 1.0709 13.973 0.992372 14.156C0.913846 14.3391 0.87256 14.5359 0.870924 14.7351C0.869287 14.9343 0.907333 15.1318 0.982842 15.3161C1.05835 15.5004 1.16981 15.6678 1.31071 15.8086C1.45162 15.9493 1.61915 16.0606 1.80353 16.136C1.98791 16.2113 2.18544 16.2492 2.38461 16.2473C2.58378 16.2455 2.78059 16.2041 2.96355 16.1254C3.14652 16.0467 3.31199 15.9323 3.45029 15.789L8.75229 10.487Z"
        fill="#FA3532"
      />
    </svg>`;
  }

  protected updated(changed: Map<string, unknown>) {
    // Start when parent opens; stop when parent closes
    if (changed.has("open")) {
      if (this.open) {
        if (!this.rec) this.start();
      } else {
        this.stopRecording();
        this.cleanupPreview();
        this.phase = "stopped";
      }
    }
  }

  private async start() {
    // feature detect
    if (!("MediaRecorder" in window) || !navigator.mediaDevices?.getUserMedia) {
      this.phase = "unsupported";
      return;
    }
    try {
      this.errMsg = "";
      // clear previous
      this.cleanupPreview();
      this.seconds = 0;
      this.levels = Array(this.bands).fill(0);
      this.phase = "recording";
      if (this.t) window.clearInterval(this.t);
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Prefer webm; fallback to mp4 if supported
      const opts: MediaRecorderOptions = {};
      if (MediaRecorder.isTypeSupported("audio/webm")) {
        opts.mimeType = "audio/webm";
        this.mimeType = "audio/webm";
      } else if (MediaRecorder.isTypeSupported("audio/mp4")) {
        opts.mimeType = "audio/mp4";
        this.mimeType = "audio/mp4";
      }

      this.rec = new MediaRecorder(this.stream, opts);
      this.chunks = [];
      this.rec.ondataavailable = (e) => e.data.size && this.chunks.push(e.data);
      this.rec.onstop = () => this.onStop();
      this.rec.start();

      // Seconds timer
      this.t = window.setInterval(() => {
        this.seconds += 1;
        // requestUpdate is not strictly needed due to @state, but keeps time snappy
        this.requestUpdate();
      }, 1000);

      // --- Accurate sound waves via Web Audio ---
      this.setupAnalyser(this.stream);
      this.startMeterLoop();
    } catch (e: any) {
      this.phase = "error";
      this.errMsg = e?.message ?? "Microphone permission denied";
    }
  }

  private stopRecording() {
    try {
      if (this.rec && this.rec.state !== "inactive") this.rec.stop();
    } catch {}
    try {
      this.stream?.getTracks().forEach((t) => t.stop());
    } catch {}
    if (this.t) window.clearInterval(this.t);
    this.t = undefined;
    this.rec = undefined;
    this.stream = undefined;
    this.chunks = [];

    // Tear down analyser & audio context
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = undefined;
    try {
      this.sourceNode?.disconnect();
    } catch {}
    try {
      this.analyser?.disconnect();
    } catch {}
    this.sourceNode = undefined;
    this.analyser = undefined;
    // Closing AudioContext releases resources on some browsers
    this.audioCtx?.close().catch(() => {});
    this.audioCtx = undefined;

    // Smoothly decay levels to 0 after stopping
    this.levels = this.levels.map(() => 0);
  }

  private onStop() {
    // create preview url (optional for inline, but handy if you want to add play later)
    if (this.chunks.length) {
      const blob = new Blob(this.chunks, { type: this.mimeType });
      this.previewUrl = URL.createObjectURL(blob);
    }
    this.phase = "stopped";
  }

  private cleanupPreview() {
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
    this.previewUrl = "";
  }

  private discard() {
    this.stopRecording();
    this.cleanupPreview();
    this.dispatchEvent(
      new CustomEvent("cancel-recorder", { bubbles: true, composed: true })
    );
  }

  private async send() {
    if (!this.chunks.length) return;
    const blob = new Blob(this.chunks, { type: this.mimeType });
    const base64 = await blobToBase64(blob);
    this.dispatchEvent(
      new CustomEvent("send-audio", {
        detail: { base64, mimeType: this.mimeType },
        bubbles: true,
        composed: true,
      })
    );
    // clean & close
    this.stopRecording();
    this.cleanupPreview();
    this.dispatchEvent(
      new CustomEvent("cancel-recorder", { bubbles: true, composed: true })
    );
  }

  private async sendNow() {
    // If still recording, stop first to flush data, then send
    if (this.rec && this.rec.state !== "inactive") {
      try {
        this.rec.stop();
      } catch {}
      // Wait a tick for onstop to populate chunks
      await new Promise((r) => setTimeout(r, 50));
    }
    await this.send();
  }

  private mmss(n: number) {
    const m = Math.floor(n / 60)
      .toString()
      .padStart(2, "0");
    const s = (n % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  }

  // kept for potential future use; not currently displayed in the compact UI
  // private get progressPct() {
  //   const max = Math.max(1, this.maxSeconds);
  //   const pct = Math.min(100, (this.seconds / max) * 100);
  //   return pct;
  // }

  // ---- Accurate “sound waves” helpers ----
  private setupAnalyser(stream: MediaStream) {
    // Some browsers (iOS Safari) need/resume on user gesture; we’re already inside one (button)
    type AudioContextCtor = typeof AudioContext;
    const AudioCtx = (window.AudioContext ||
      (window as any).webkitAudioContext) as AudioContextCtor;
    this.audioCtx = new AudioCtx();
    this.analyser = this.audioCtx.createAnalyser();
    // Lower fftSize gives fewer bins and cheaper work; 512 -> 256 bins frequencyDomain
    this.analyser.fftSize = 512;
    this.analyser.smoothingTimeConstant = 0.8; // smooth out jitter
    this.sourceNode = this.audioCtx.createMediaStreamSource(stream);
    this.sourceNode.connect(this.analyser);
  }

  private startMeterLoop() {
    if (!this.analyser) return;
    const analyser = this.analyser;
    const bins = new Uint8Array(analyser.frequencyBinCount); // 256 for fftSize 512
    const bands = this.bands;

    const tick = () => {
      if (this.phase !== "recording" || !this.analyser) {
        // Stop updating when not recording
        return;
      }

      // Use frequency data for a classic “bar EQ” look.
      analyser.getByteFrequencyData(bins);

      // Map the linear FFT bins into 16 roughly log-spaced bands
      const newLevels: number[] = [];
      // Log spacing: emphasize lows & mids; tweak base and factor as desired
      const minIndex = 2; // ignore the very lowest DC bins
      const maxIndex = bins.length - 1;
      for (let b = 0; b < bands; b++) {
        // exponential band edges
        const t0 = b / bands;
        const t1 = (b + 1) / bands;
        const i0 = Math.floor(minIndex + (maxIndex - minIndex) * t0 * t0);
        const i1 =
          Math.floor(minIndex + (maxIndex - minIndex) * t1 * t1) || i0 + 1;

        let sum = 0;
        let count = 0;
        for (let i = i0; i < i1; i++) {
          sum += bins[i];
          count++;
        }
        const avg = count ? sum / count : 0;

        // Convert 0..255 to 0..100%, apply a gentle nonlinearity to feel responsive
        const pct = Math.min(100, Math.max(0, Math.pow(avg / 255, 0.8) * 100));
        // Keep a tiny floor so silent doesn’t look dead
        newLevels.push(Math.max(4, Math.round(pct)));
      }

      // Update state (Lit batches; this triggers re-render of bar heights)
      this.levels = newLevels;

      this.raf = requestAnimationFrame(tick);
    };

    // Kick off the loop
    this.raf = requestAnimationFrame(tick);
  }

  render() {
    if (!this.open) return null;

    if (this.phase === "unsupported") {
      return html`<div class="rec-row">
        <div class="left">
          <span class="dot red"></span>
          <span class="time">Voice not supported</span>
        </div>
        <div class="right">
          <button
            class="btn icon danger"
            @click=${this.discard}
            title="Close"
            aria-label="Close"
          >
            ${this.XIcon(17)}
          </button>
        </div>
      </div>`;
    }
    if (this.phase === "error") {
      return html`<div class="rec-row">
        <div class="left">
          <span class="dot red"></span>
          <span class="time">${this.errMsg}</span>
        </div>
        <div class="right">
          <button
            class="btn icon danger"
            @click=${this.discard}
            title="Close"
            aria-label="Close"
          >
            ${this.XIcon(17)}
          </button>
        </div>
      </div>`;
    }

    return html`
      <div class="hollo-rec-row" role="group" aria-label="Recording controls">
        <div class="hollo-left-recorder">
          <div class="hollo-eq">
            ${this.levels.map(
              (level, i) =>
                html`<span
                  class="hollo-bar"
                  style="--i:${i}; height:${level}%"
                ></span>`
            )}
          </div>
          <span class="hollo-time">${this.mmss(this.seconds)}</span>
        </div>

        <div class="hollo-right">
          <button
            class="hollo-btn hollo-icon hollo-cancel"
            @click=${this.discard}
            title="Discard"
            aria-label="Discard"
          >
            ${xIcon}
          </button>
          <button
            class="hollo-btn hollo-icon hollo-confirm"
            @click=${this.sendNow}
            title="Send"
            aria-label="Send"
          >
            ${checkIcon}
          </button>
        </div>
      </div>
    `;
  }

  static styles = holloInlineRecorderStyles;
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => {
      const result = String(fr.result || "");
      const idx = result.indexOf(",");
      resolve(idx >= 0 ? result.slice(idx + 1) : result); // strip data: prefix
    };
    fr.onerror = reject;
    fr.readAsDataURL(blob);
  });
}

// icons
// old icons removed in compact design
const xIcon = html`<svg
  viewBox="0 0 24 24"
  width="18"
  height="18"
  fill="currentColor"
  aria-hidden="true"
>
  <path
    d="M18.3 5.7L12 12l6.3 6.3-1.4 1.4L10.6 13.4 4.3 19.7 2.9 18.3 9.2 12 2.9 5.7 4.3 4.3 10.6 10.6 16.9 4.3z"
  />
</svg>`;
const checkIcon = html`<svg
  viewBox="0 0 24 24"
  width="18"
  height="18"
  fill="currentColor"
  aria-hidden="true"
>
  <path d="M9 16.2l-3.5-3.5-1.4 1.4L9 19 20.3 7.7l-1.4-1.4z" />
</svg>`;

declare global {
  interface HTMLElementTagNameMap {
    "hollo-inline-recorder": HolloInlineRecorder;
  }
}
